import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, FolderOpen, Plus, Database, Sparkles, Pencil, Trash2 } from 'lucide-react';
import { IdealCustomerProfile, Dataset } from '../../../types';

interface SidebarICPSectionProps {
  icpProfiles: IdealCustomerProfile[];
  datasets: Dataset[];
  isExpanded: boolean;
  expandedIcpIds: Set<string>;
  onToggleSection: () => void;
  onToggleIcp: (icpId: string) => void;
  onOpenWizard: () => void;
  onOpenWithIcp: (icp: IdealCustomerProfile) => void;
  onEditIcp: (e: React.MouseEvent, icp: IdealCustomerProfile) => void;
  onDeleteIcp: (e: React.MouseEvent, icp: IdealCustomerProfile) => void;
}

export const SidebarICPSection: React.FC<SidebarICPSectionProps> = ({
  icpProfiles,
  datasets,
  isExpanded,
  expandedIcpIds,
  onToggleSection,
  onToggleIcp,
  onOpenWizard,
  onOpenWithIcp,
  onEditIcp,
  onDeleteIcp,
}) => {
  const navigate = useNavigate();

  if (icpProfiles.length === 0) return null;

  return (
    <div className="mt-2.5">
      <div className="px-4 mb-1.5 flex items-center justify-between">
        <button
          onClick={onToggleSection}
          className="flex items-center gap-1.5 group"
        >
          <ChevronRight
            className={`w-2.5 h-2.5 text-neutral-400 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
          />
          <h3 className="text-2xs font-semibold uppercase tracking-wider text-neutral-400 group-hover:text-neutral-600 transition">
            ICPs
          </h3>
          <span className="text-2xs text-neutral-400">
            {icpProfiles.length}
          </span>
        </button>
        <button
          onClick={() => {
            navigate('/datasets');
            onOpenWizard();
          }}
          className="p-1 rounded text-neutral-500 hover:bg-neutral-200/60 transition"
          title="Ask AI for ideas"
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>

      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden min-h-0">
          <div className="px-3 space-y-0.5 pb-1">
            {icpProfiles.map(icp => {
            const icpDatasets = datasets.filter(d => d.icp_id === icp.id);
            const isIcpExpanded = expandedIcpIds.has(icp.id);

            return (
              <div key={icp.id}>
                <div
                  role="button"
                  tabIndex={0}
                  className="group w-full flex items-center gap-2.5 py-2 px-2.5 rounded-lg transition hover:bg-neutral-100/80 cursor-pointer"
                  onClick={() => onToggleIcp(icp.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onToggleIcp(icp.id);
                    }
                  }}
                >
                  <div className="relative w-4.5 h-4.5 flex-shrink-0">
                    <FolderOpen
                      className={`absolute inset-0 w-4.5 h-4.5 transition-opacity group-hover:opacity-0 ${
                        isIcpExpanded ? 'text-neutral-600' : 'text-neutral-500'
                      }`}
                    />
                    <ChevronRight
                      className={`absolute inset-0 w-4.5 h-4.5 text-neutral-600 opacity-0 group-hover:opacity-100 transition ${
                        isIcpExpanded ? 'rotate-90' : ''
                      }`}
                    />
                  </div>

                  <span className="flex-1 text-left text-13 truncate text-neutral-600">
                    {icp.name}
                  </span>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {icpDatasets.length > 0 && (
                      <div
                        className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-blue-100"
                        title={`${icpDatasets.length} dataset${icpDatasets.length > 1 ? 's' : ''}`}
                      >
                        <Database className="w-2 h-2 text-blue-600" />
                        <span className="text-2xs font-medium text-blue-600">
                          {icpDatasets.length}
                        </span>
                      </div>
                    )}

                    <div className="hidden group-hover:flex items-center gap-0.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/datasets');
                          onOpenWithIcp(icp);
                        }}
                        className="p-1 rounded hover:bg-blue-100 transition"
                        title="Generate signals"
                      >
                        <Sparkles className="w-2.5 h-2.5 text-blue-500" />
                      </button>
                      <button
                        onClick={(e) => onEditIcp(e, icp)}
                        className="p-1 rounded hover:bg-neutral-200 transition"
                        title="Edit ICP"
                      >
                        <Pencil className="w-2.5 h-2.5 text-neutral-500" />
                      </button>
                      <button
                        onClick={(e) => onDeleteIcp(e, icp)}
                        className="p-1 rounded hover:bg-error-100 transition"
                        title="Delete ICP"
                      >
                        <Trash2 className="w-2.5 h-2.5 text-error-500" />
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  className="grid transition-[grid-template-rows] duration-150 ease-out"
                  style={{ gridTemplateRows: isIcpExpanded ? '1fr' : '0fr' }}
                >
                  <div className="overflow-hidden min-h-0">
                    <div className="ml-5 pl-3 border-l border-neutral-200 mt-0.5 mb-1">
                      {icpDatasets.length === 0 ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/datasets');
                          onOpenWithIcp(icp);
                        }}
                        className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-blue-50 transition w-full text-left group/add"
                      >
                        <Plus className="w-3 h-3 text-neutral-400 group-hover/add:text-blue-500 transition" />
                        <span className="text-2xs text-neutral-400 group-hover/add:text-blue-500 transition">
                          Generate signals
                        </span>
                      </button>
                    ) : (
                      <div className="space-y-0.5">
                        {icpDatasets.map((dataset) => (
                          <button
                            key={dataset.id}
                            className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-neutral-100 transition w-full text-left"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/datasets/${dataset.id}`);
                            }}
                            title={dataset.description || dataset.name}
                          >
                            <Database className="w-3 h-3 text-blue-600" />
                            <span className="text-xs text-neutral-600 truncate flex-1">
                              {dataset.name}
                            </span>
                            {dataset.lead_count !== undefined && dataset.lead_count > 0 && (
                              <span className="text-2xs text-neutral-400">
                                {dataset.lead_count}
                              </span>
                            )}
                          </button>
                        ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
};
